import { environmentLabel, errorCategoryLabel, featureLabel, serviceLabel } from 'lib';
import { Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { badge, callOutcomeTone, fmtDate, fmtDuration, httpCodeColor } from './helpers';

const th = 'text-left text-[10px] font-semibold text-[#97A3B5] uppercase tracking-wide px-3 py-2';
const td = 'px-3 py-2 text-[13px] text-[#353E4A] align-top';

const DetailRow = ({ label, children }) => (
  <div className="flex gap-2">
    <span className="text-[11px] font-semibold text-[#97A3B5] uppercase min-w-[130px]">
      {label}
    </span>
    <span className="text-[12px] text-[#353E4A] break-all">{children}</span>
  </div>
);

const CallsTable = ({ rows, loading, onTraceRun, onTraceSubject }) => {
  const { t } = useTranslation();
  const [openId, setOpenId] = useState(null);

  if (loading) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center text-[13px] text-[#97A3B5]">
        {t('admin.activityLog.loading', { defaultValue: 'Loading…' })}
      </div>
    );
  }

  if (!rows?.length) {
    return (
      <div className="bg-white border border-[#E5E7EB] rounded-xl p-8 text-center text-[13px] text-[#97A3B5]">
        {t('admin.activityLog.calls.empty', {
          defaultValue: 'No provider calls recorded for these filters.'
        })}
      </div>
    );
  }

  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl overflow-x-auto">
      <table className="w-full min-w-[900px] border-collapse">
        <thead className="bg-[#F8FAFC] border-b border-[#E5E7EB]">
          <tr>
            <th className={th}>{t('admin.activityLog.calls.when', { defaultValue: 'When' })}</th>
            <th className={th}>
              {t('admin.activityLog.calls.service', { defaultValue: 'Service' })}
            </th>
            <th className={th}>
              {t('admin.activityLog.calls.operation', { defaultValue: 'Operation' })}
            </th>
            <th className={th}>
              {t('admin.activityLog.calls.feature', { defaultValue: 'Caused by' })}
            </th>
            <th className={th}>{t('admin.activityLog.calls.env', { defaultValue: 'Env' })}</th>
            <th className={th}>
              {t('admin.activityLog.calls.duration', { defaultValue: 'Took' })}
            </th>
            <th className={th}>
              {t('admin.activityLog.calls.outcome', { defaultValue: 'Outcome' })}
            </th>
            <th className={th} />
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const open = openId === row._id;
            return (
              <Fragment key={row._id}>
                <tr className="border-b border-[#F1F5F9] hover:bg-[#FAFBFC]">
                  <td className={`${td} whitespace-nowrap`}>{fmtDate(row.startedAt)}</td>
                  <td className={td}>{serviceLabel(row.service)}</td>
                  <td className={`${td} font-mono text-[12px]`}>{row.operation}</td>
                  <td className={td}>{featureLabel(row.feature)}</td>
                  <td className={td}>
                    <span className={badge('muted')}>{environmentLabel(row.environment)}</span>
                  </td>
                  <td className={`${td} whitespace-nowrap`}>{fmtDuration(row.durationMs)}</td>
                  <td className={td}>
                    <div className="flex items-center gap-2">
                      <span className={badge(callOutcomeTone(row.outcome))}>{row.outcome}</span>
                      {row.httpStatusCode ? (
                        <span
                          className={`text-[12px] font-mono ${httpCodeColor(row.httpStatusCode)}`}
                        >
                          {row.httpStatusCode}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className={td}>
                    <button
                      type="button"
                      onClick={() => setOpenId(open ? null : row._id)}
                      className="text-[12px] text-[#2563EB] hover:underline"
                    >
                      {open
                        ? t('admin.activityLog.hide', { defaultValue: 'Hide' })
                        : t('admin.activityLog.details', { defaultValue: 'Details' })}
                    </button>
                  </td>
                </tr>
                {open && (
                  <tr className="border-b border-[#F1F5F9] bg-[#FAFBFC]">
                    <td className={td} colSpan={8}>
                      <div className="flex flex-col gap-1 py-1">
                        <DetailRow
                          label={t('admin.activityLog.detail.instance', {
                            defaultValue: 'Instance'
                          })}
                        >
                          {row.instanceId || '—'}
                        </DetailRow>
                        <DetailRow
                          label={t('admin.activityLog.detail.release', { defaultValue: 'Release' })}
                        >
                          {row.release || '—'}
                        </DetailRow>
                        {/*
                          Shown so an admin can see two environments authenticating with the same
                          key — the reason this feature exists. It is a one-way digest: no part of
                          the key appears in it.
                        */}
                        <DetailRow
                          label={t('admin.activityLog.detail.credential', {
                            defaultValue: 'Credential'
                          })}
                        >
                          <span className="font-mono">{row.credentialFingerprint || '—'}</span>
                        </DetailRow>
                        <DetailRow
                          label={t('admin.activityLog.detail.errorCategory', {
                            defaultValue: 'Error category'
                          })}
                        >
                          {errorCategoryLabel(row.errorCategory)}
                        </DetailRow>
                        {row.errorSummary ? (
                          <DetailRow
                            label={t('admin.activityLog.detail.errorSummary', {
                              defaultValue: 'Error detail'
                            })}
                          >
                            {row.errorSummary}
                          </DetailRow>
                        ) : null}
                        <DetailRow
                          label={t('admin.activityLog.detail.subjects', {
                            defaultValue: 'Concerned'
                          })}
                        >
                          {row.subjects?.length ? (
                            <span className="flex flex-wrap gap-1">
                              {row.subjects.map((s) => (
                                <button
                                  key={`${s.kind}:${s.id}`}
                                  type="button"
                                  onClick={() => onTraceSubject?.(s.id)}
                                  className="font-mono text-[11px] text-[#2563EB] hover:underline"
                                >
                                  {s.kind}:{s.id}
                                </button>
                              ))}
                            </span>
                          ) : (
                            '—'
                          )}
                        </DetailRow>
                        {row.runId ? (
                          <DetailRow
                            label={t('admin.activityLog.detail.run', {
                              defaultValue: 'Scheduled run'
                            })}
                          >
                            <button
                              type="button"
                              onClick={() => onTraceRun?.(row.runId)}
                              className="text-[12px] text-[#2563EB] hover:underline font-mono"
                            >
                              {String(row.runId)}
                            </button>
                          </DetailRow>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default CallsTable;
